import React, { useState } from 'react';
import { ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { UptimeCheck } from './types';
import { formatResponseTime, formatCheckDate } from './types';

interface LastCheckDetailsProps {
  check: UptimeCheck | null;
}

interface TimingRow {
  label: string;
  value: number | null;
  color: string;
  max: number;
}

function buildTimingRows(check: UptimeCheck): TimingRow[] {
  const total = check.total_time_ms ?? 0;
  const maxVal = Math.max(total, 1);
  return [
    { label: 'DNS Lookup', value: check.dns_ms, color: '#eab308', max: maxVal },
    { label: 'TCP Connection', value: check.tcp_ms, color: '#ef4444', max: maxVal },
    { label: 'SSL Handshake', value: check.ssl_ms, color: '#4b5563', max: maxVal },
    { label: 'Server Processing', value: check.server_ms, color: '#f97316', max: maxVal },
    { label: 'Content Download', value: check.download_ms, color: '#f472b6', max: maxVal },
  ];
}

export const LastCheckDetails: React.FC<LastCheckDetailsProps> = ({ check }) => {
  const [expanded, setExpanded] = useState(false);

  if (!check) {
    return (
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <p className="text-sm text-muted-foreground">No check data recorded yet.</p>
      </div>
    );
  }

  const isUp = check.status === 'up';
  const timingRows = buildTimingRows(check);
  const hasBreakdown = timingRows.some(r => r.value !== null);

  const requestRows = timingRows.slice(0, 3);
  const responseRows = timingRows.slice(3);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-foreground">Last check</span>
          <div className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${isUp ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'text-red-600 bg-red-50 dark:bg-red-900/20'}`}>
            {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isUp ? 'Up' : 'Down'}
          </div>
        </div>
        {hasBreakdown && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? 'Hide details' : 'See details'}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      <div className="px-5 py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm border-b border-border">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Status code</span>
          {check.status_code ? (
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${check.status_code < 400 ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
              {check.status_code}
            </span>
          ) : (
            <span className="text-xs font-semibold text-foreground">—</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Total time</span>
          <span className="text-xs font-semibold text-foreground">{formatResponseTime(check.total_time_ms)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Server IP</span>
          <span className="text-xs font-semibold text-foreground">{check.server_ip || '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Redirects</span>
          <span className="text-xs font-semibold text-foreground">{check.redirects === 0 ? 'No redirects' : `${check.redirects} redirect${check.redirects > 1 ? 's' : ''}`}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Last check date</span>
          <span className="text-xs font-semibold text-foreground">{formatCheckDate(check.created_at)}</span>
        </div>
      </div>

      <AnimatePresence>
        {expanded && hasBreakdown && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 space-y-5">
              <TimingSection label="Request" rows={requestRows} />
              <TimingSection label="Response" rows={responseRows} />
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-sm font-bold text-foreground">Total time</span>
                <span className="text-sm font-bold text-foreground tabular-nums">{formatResponseTime(check.total_time_ms)}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface TimingSectionProps {
  label: string;
  rows: { label: string; value: number | null; color: string; max: number }[];
}

const TimingSection: React.FC<TimingSectionProps> = ({ label, rows }) => {
  const hasAny = rows.some(r => r.value !== null);
  if (!hasAny) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Duration</span>
      </div>
      {rows.map(row => (
        row.value !== null && (
          <div key={row.label} className="flex items-center gap-3">
            <span className="text-xs text-foreground w-36 flex-shrink-0">{row.label}</span>
            <div className="flex-1 h-2 bg-muted/40 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.max(2, (row.value / row.max) * 100)}%`,
                  backgroundColor: row.color,
                }}
              />
            </div>
            <span className="text-xs font-semibold text-foreground tabular-nums w-14 text-right">
              {formatResponseTime(row.value)}
            </span>
          </div>
        )
      ))}
    </div>
  );
};
